import { decrypt, encrypt } from "../utils/other";
import { getTokenObj, isNewerToken, tokenUserId } from "./tokenManager";
import { saveSession } from "../utils/saveSession";
import {
  refreshTokens,
  isGenuineRefreshFailure,
  isRefreshRaceLost,
} from "./refreshTokens";
import { getCookie } from "../utils/cookieUtils";
import sessionExpired from "../Core/sessionExpired";
import { FeatureFlag } from "./featureFlags/activeFlags";
import { FLAGS } from "./featureFlags/registry";
import { LOGOUT_REASONS, refreshFailureDetails } from "../utils/authEventLog";
import {
  recoveryCheckDetails,
  logSessionRecovered,
  noLocalSessionDetails,
} from "./recoveryDiagnostics";

/**
 * Deduplicating token refresh coordinator for MFE mode.
 *
 * Both portal's own API interceptor (createApiClient.js) and Sandbox's
 * delegated refresh (via onTokenRefreshNeeded → handleSandboxTokenRefresh)
 * call this function. Because a single _mfeRefreshPromise is shared, only
 * ONE SSO call fires regardless of how many concurrent 401s occur across
 * both apps.
 *
 * On failure:
 *   - Calls sessionExpired() exactly once (portal's swal dialog).
 *   - Re-throws with err.sessionExpiredHandled = true so callers
 *     (sandbox's and portal's catch blocks) skip their own sessionExpired()
 *     calls and avoid double dialogs or conflicting window.location navigations.
 *
 * HOW TO REMOVE (when retiring the MFE):
 *   1. Delete this file.
 *   2. In SandboxRenderer.jsx, delete the import and restore the old
 *      handleSandboxTokenRefresh body (or just delete the whole block and
 *      remove the onTokenRefreshNeeded prop from mount()).
 *   3. In createApiClient.js, remove the import and the
 *      `if (window.__SANDBOX_IS_PLUGIN__)` branch — keep only the else branch.
 */
let _mfeRefreshPromise = null;

// Persists a fresh token pair to every store both apps read: localStorage.user
// (via the common saveSession, which also re-writes the shared cookies for
// students), the embedded Sandbox's sandbox_user, and the live-update event.
function syncTokensToStores(accessToken, refreshToken) {
  const prevUser = getTokenObj() || {};
  saveSession({ accessToken, refreshToken, userData: prevUser });

  const prevSandbox = decrypt(localStorage.getItem("sandbox_user")) || {};
  localStorage.setItem(
    "sandbox_user",
    encrypt({ ...prevSandbox, ACCESS_TOKEN: accessToken, REFRESH_TOKEN: refreshToken })
  );
  window.dispatchEvent(new Event("sandbox_user_update"));
}

// ── Lost-refresh-race recovery ──────────────────────────────────────────────
// With rotating refresh tokens, one session lineage is shared by several JS
// contexts (other portal tabs via localStorage; a standalone Sandbox tab via
// the shared cookies). This coordinator only dedupes refreshes INSIDE one
// page, so two contexts can race — the backend rotates for the winner and
// answers the loser with `recentDeletion` (surfaced as `isRefreshRaceLost`).
// The loser must NOT log out: the session is alive and the winner holds the
// fresh pair. Poll the sibling stores for a pair strictly NEWER (JWT iat) than
// the failed one, for the SAME user, and adopt it. Returns tokens or null.
const RACE_RECOVERY_ATTEMPTS = 3;
const RACE_RECOVERY_DELAY_MS = 700;

// ── Stale-tab recovery (auth_stale_tab_recovery) ────────────────────────────
// The race recovery only runs on cy-sso's `recentDeletion` signal, which lasts
// ONE MINUTE after a rotation. A tab that stays focused never fires the
// focus/visibility cookie resync either (authSyncListener), so when the other
// app rotates the tokens and this tab's next request comes more than a minute
// later, its refresh is answered with a plain 401 — indistinguishable from a
// dead session, although the live pair is sitting in the shared cookies.
// With the flag ON the same sibling-store check runs (once, no polling — the
// winner persisted long ago) before the refresh call, which then refreshes
// with the adopted pair, and again if cy-sso rejects the refresh. A genuine
// logout still logs out: clearAuthCookies() wipes the shared cookies, so there
// is nothing newer to adopt.
export const staleTabRecoveryOn = () =>
  FeatureFlag.isFeatureActive(FLAGS.AUTH_STALE_TAB_RECOVERY.name);

export async function adoptTokensFromSiblingContext(
  failedAccessToken,
  attempts = RACE_RECOVERY_ATTEMPTS
) {
  for (let attempt = 0; attempt < attempts; attempt++) {
    // (a) Same-origin sibling tab: it wrote the fresh pair to localStorage.
    const stored = getTokenObj();
    if (
      stored?.ACCESS_TOKEN &&
      stored.ACCESS_TOKEN !== failedAccessToken &&
      isNewerToken(stored.ACCESS_TOKEN, failedAccessToken) &&
      tokenUserId(stored.ACCESS_TOKEN) === tokenUserId(failedAccessToken)
    ) {
      return {
        accessToken: stored.ACCESS_TOKEN,
        refreshToken: stored.REFRESH_TOKEN,
        source: "localStorage",
      };
    }

    // (b) The other app (cross-origin): its refresh re-wrote the shared
    // cookies. Same-user + strictly-newer only — a DIFFERENT user's cookie is
    // a profile switch, owned by authSyncListener/bootstrap, not by recovery.
    const cookieAccess = getCookie("Authorization");
    const cookieRefresh = getCookie("refresh-token");
    if (
      cookieAccess &&
      cookieRefresh &&
      cookieAccess !== failedAccessToken &&
      isNewerToken(cookieAccess, failedAccessToken) &&
      tokenUserId(cookieAccess) === tokenUserId(failedAccessToken)
    ) {
      return { accessToken: cookieAccess, refreshToken: cookieRefresh, source: "cookie" };
    }

    // (c) The checkout/plugins MFE (cy-plugins): it shares this lineage via
    // the plain-JSON `cy-user` key and refreshes with its OWN client, so its
    // race win lands there first (it deliberately never touches the encrypted
    // `user` key). Same same-user + strictly-newer guards.
    try {
      const cyUser = JSON.parse(localStorage.getItem("cy-user") || "null");
      const pluginAccess = cyUser?.ACCESS_TOKEN;
      const pluginRefresh = cyUser?.REFRESH_TOKEN;
      if (
        pluginAccess &&
        pluginRefresh &&
        pluginAccess !== failedAccessToken &&
        isNewerToken(pluginAccess, failedAccessToken) &&
        tokenUserId(pluginAccess) === tokenUserId(failedAccessToken)
      ) {
        return { accessToken: pluginAccess, refreshToken: pluginRefresh, source: "cy-user" };
      }
    } catch (_) {
      /* cy-user absent or not JSON — ignore */
    }

    // Winner may not have persisted yet — wait and re-check.
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, RACE_RECOVERY_DELAY_MS));
    }
  }
  return null;
}

/**
 * @param {Object} [context] For the logout log only: `endpoint` (the request
 *   that 401'd) and `caller` ("studentportal" | "sandbox"). A refresh already
 *   in flight keeps the first caller's context.
 */
export function doMfeTokenRefresh(context = {}) {
  if (_mfeRefreshPromise) return _mfeRefreshPromise;

  _mfeRefreshPromise = (async () => {
    // For the diagnostics (recoveryDiagnostics): the token this page held, and
    // whether a sibling's pair replaced it before the refresh.
    let staleToken = null;
    let newer = null;
    let tokenObj = null;
    try {
      tokenObj = getTokenObj();
      if (!tokenObj || !tokenObj.ACCESS_TOKEN) {
        // No local token → session is genuinely gone → log out (tag it so the
        // genuine-failure check below treats it as such, not as offline).
        throw Object.assign(new Error("No portal tokens available for refresh"), {
          refreshAuthFailed: true,
          noLocalSession: true,
        });
      }

      let accessToken, refreshToken;
      staleToken = tokenObj.ACCESS_TOKEN;
      // Stale tab: the other app already rotated this session and holds the
      // live pair. Refresh with THAT pair instead of ours, whose refresh token
      // cy-sso will reject. The adopted pair still goes through the refresh
      // rather than straight back to the caller: its access token may have
      // expired too (the other app rotated over an hour ago), and cy-sso
      // hands a still-valid pair back unchanged.
      newer = staleTabRecoveryOn()
        ? await adoptTokensFromSiblingContext(tokenObj.ACCESS_TOKEN, 1)
        : null;
      if (newer) {
        tokenObj = {
          ...tokenObj,
          ACCESS_TOKEN: newer.accessToken,
          REFRESH_TOKEN: newer.refreshToken,
        };
      }
      try {
        ({ accessToken, refreshToken } = await refreshTokens(tokenObj));
        if (newer) {
          logSessionRecovered({
            stage: "before-refresh",
            source: newer.source,
            refreshPath: "mfe-coordinator",
            staleToken,
          });
        }
      } catch (refreshErr) {
        // Lost refresh race → adopt the winning context's tokens instead of
        // failing. On no recovery, mark the error so callers don't repeat
        // this exact store-polling on the same page.
        const raceLost = isRefreshRaceLost(refreshErr);
        // cy-sso rejected the refresh token outright. Behind the flag, check
        // the sibling stores once more before believing the session is dead
        // (the other app may have rotated between the check above and this
        // call landing).
        const refreshRejected =
          staleTabRecoveryOn() && refreshErr?.response?.status === 401;
        if (!raceLost && !refreshRejected) throw refreshErr;
        const adopted = await adoptTokensFromSiblingContext(
          tokenObj.ACCESS_TOKEN,
          raceLost ? RACE_RECOVERY_ATTEMPTS : 1
        );
        if (!adopted) {
          if (raceLost) refreshErr.raceRecoveryAttempted = true;
          throw refreshErr;
        }
        ({ accessToken, refreshToken } = adopted);
        logSessionRecovered({
          stage: raceLost ? "race-lost" : "after-reject",
          source: adopted.source,
          refreshPath: "mfe-coordinator",
          staleToken,
        });
      }

      // Update portal tokens through the common saveSession so localStorage.user,
      // cy-user, profile_pic, and the shared cross-app cookies all stay in sync —
      // and sync to sandbox_user so the embedded Sandbox picks up new tokens
      // without an extra refresh (this coordinator runs only in MFE mode).
      syncTokensToStores(accessToken, refreshToken);

      return { accessToken, refreshToken };
    } catch (err) {
      // Log out only on a GENUINE refresh failure — the server responded
      // (401 / 500 / 200-valid:false) or there's no local token. A pure
      // connectivity error (offline / no `response`) keeps the session. Mark the
      // error so both apps' catch blocks skip their own sessionExpired() call
      // (no double dialogs / conflicting navigations).
      if (isGenuineRefreshFailure(err) && !err.sessionExpiredHandled) {
        if (err.noLocalSession) {
          sessionExpired(LOGOUT_REASONS.NO_LOCAL_SESSION, {
            refreshPath: "mfe-coordinator",
            ...noLocalSessionDetails(context),
          });
        } else {
          sessionExpired(LOGOUT_REASONS.REFRESH_FAILED, {
            ...refreshFailureDetails(err),
            staleTabRecovery: staleTabRecoveryOn(),
            refreshPath: "mfe-coordinator",
            // Taken now, while the stores still hold what recovery saw.
            ...recoveryCheckDetails(tokenObj?.ACCESS_TOKEN, {
              adoptedBeforeRefresh: !!newer,
            }),
          });
        }
        err.sessionExpiredHandled = true;
      }
      throw err;
    } finally {
      _mfeRefreshPromise = null;
    }
  })();

  return _mfeRefreshPromise;
}
